import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Lock,
  Loader2,
  LogOut,
  Phone,
  Car,
  Calendar as CalIcon,
  MapPin,
  Search,
  Check,
  X,
  Trash2,
  FileText,
  RefreshCw,
  Download,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sod_admin_token";
const LAST_EXPORT_KEY = "sod_last_export_date";

const STATUS_OPTIONS = ["pending", "confirmed", "completed", "cancelled"];

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  if (!token) {
    return <Login onLogin={(t) => setToken(t)} />;
  }

  return <Dashboard token={token} onLogout={() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    navigate("/admin");
  }} />;
}

// ---------- Login ----------
function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const token = res.data.token;
      localStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Wrong password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 grid place-items-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white p-10"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="h-9 w-9 bg-blue-600 grid place-items-center text-white font-display font-black">
            SO
          </div>
          <div className="font-display font-black tracking-tight">
            Southern Oregon Detailing
          </div>
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-blue-600 mt-8 mb-3">
          Admin Access
        </div>
        <h1 className="font-display font-black text-3xl tracking-tight mb-6">
          Sign in to view bookings.
        </h1>
        <label className="block">
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-slate-500 mb-2 block">
            Password
          </span>
          <div className="relative">
            <Lock
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-slate-300 pl-10 pr-3 py-3 text-sm outline-none focus:border-blue-600 transition-colors"
              placeholder="Enter password"
              autoFocus
            />
          </div>
        </label>
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={submitting}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold px-6 py-3.5 text-sm transition-colors"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Signing in…
            </>
          ) : (
            "Sign in"
          )}
        </button>
        <a
          href="/"
          className="mt-6 block text-center text-xs text-slate-500 hover:text-slate-900"
        >
          ← Back to site
        </a>
      </form>
    </div>
  );
}

// ---------- Dashboard ----------
function Dashboard({ token, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers: authHeader });
      setBookings((prev) => {
        const prevIds = new Set(prev.map((b) => b.id));
        const newOnes = res.data.filter((b) => !prevIds.has(b.id));
        if (silent && newOnes.length > 0) {
          toast.success(
            `${newOnes.length} new booking${newOnes.length > 1 ? "s" : ""}!`
          );
        }
        return res.data;
      });
      setLastUpdated(new Date());
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else if (!silent) {
        toast.error("Failed to load bookings.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 20 seconds so new bookings appear without manual reload
    const interval = setInterval(() => load(true), 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${id}`,
        { status },
        { headers: authHeader }
      );
      setBookings((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status } : b))
      );
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers: authHeader });
      setBookings((prev) => prev.filter((b) => b.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportCsv = () => {
    if (bookings.length === 0) {
      toast.error("No bookings to export.");
      return;
    }
    const headers = [
      "Name",
      "Phone",
      "Email",
      "Service",
      "Vehicle",
      "Date",
      "Time",
      "Location",
      "Notes",
      "Status",
      "Booked At",
    ];
    const escape = (v) => {
      const s = (v ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = bookings.map((b) =>
      [
        b.name,
        b.phone,
        b.email,
        b.service,
        b.vehicle,
        b.date,
        b.time,
        b.location,
        b.notes,
        b.status,
        b.created_at,
      ]
        .map(escape)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    link.download = `southern-oregon-detailing-bookings-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${bookings.length} bookings.`);
  };

  const counts = useMemo(() => {
    return {
      total: bookings.length,
      pending: bookings.filter((b) => b.status === "pending").length,
      confirmed: bookings.filter((b) => b.status === "confirmed").length,
      completed: bookings.filter((b) => b.status === "completed").length,
    };
  }, [bookings]);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        b.name?.toLowerCase().includes(q) ||
        b.phone?.toLowerCase().includes(q) ||
        b.vehicle?.toLowerCase().includes(q) ||
        b.service?.toLowerCase().includes(q) ||
        b.location?.toLowerCase().includes(q)
      );
    });
  }, [bookings, query, statusFilter]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="container mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-blue-600 grid place-items-center text-white font-display font-black text-sm">
              SO
            </div>
            <div className="leading-none">
              <div className="font-display font-black text-base tracking-tight">
                Admin Dashboard
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-blue-600 font-semibold">
                Bookings
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-600 text-white px-3 py-2 text-xs font-bold transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={() => load(true)}
              data-testid="admin-refresh"
              disabled={refreshing}
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 px-3 py-2 text-xs font-bold transition-colors disabled:opacity-60"
            >
              <RefreshCw
                size={14}
                className={refreshing ? "animate-spin" : ""}
              />
              Refresh
            </button>
            <a
              href="/"
              className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2"
            >
              View site
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 px-3 py-2 text-xs font-bold transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-8">
          <Stat label="Total" value={counts.total} testid="stat-total" />
          <Stat
            label="Pending"
            value={counts.pending}
            accent="text-blue-600"
            testid="stat-pending"
          />
          <Stat label="Confirmed" value={counts.confirmed} testid="stat-confirmed" />
          <Stat label="Completed" value={counts.completed} testid="stat-completed" />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, phone, vehicle, service…"
              className="w-full pl-10 pr-3 py-3 text-sm border border-slate-300 bg-white outline-none focus:border-blue-600"
            />
          </div>
          <div className="flex gap-1 bg-white border border-slate-300 p-1">
            {["all", ...STATUS_OPTIONS].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-3 py-2 text-xs font-bold capitalize transition-colors ${
                  statusFilter === s
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-slate-500">
              <Loader2 className="animate-spin inline mr-2" size={16} /> Loading bookings…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <div className="font-display font-bold text-lg text-slate-900 mb-1">
                No bookings yet.
              </div>
              <div className="text-sm text-slate-500">
                When customers book through the site, they'll show up here.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-bookings-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
                    <th className="text-left py-3 px-4">Customer</th>
                    <th className="text-left py-3 px-4">Service</th>
                    <th className="text-left py-3 px-4">Vehicle</th>
                    <th className="text-left py-3 px-4">When</th>
                    <th className="text-left py-3 px-4">Location</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr
                      key={b.id}
                      data-testid={`booking-row-${b.id}`}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                    >
                      <td className="py-4 px-4">
                        <div className="font-bold text-slate-900">{b.name}</div>
                        <a
                          href={`tel:${b.phone}`}
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"
                        >
                          <Phone size={11} /> {b.phone}
                        </a>
                      </td>
                      <td className="py-4 px-4 text-slate-700">{b.service}</td>
                      <td className="py-4 px-4 text-slate-700">{b.vehicle}</td>
                      <td className="py-4 px-4 text-slate-700 whitespace-nowrap">
                        {b.date}
                        <div className="text-xs text-slate-500">{b.time}</div>
                      </td>
                      <td className="py-4 px-4 text-slate-700">{b.location || "-"}</td>
                      <td className="py-4 px-4">
                        <StatusBadge status={b.status} />
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => setSelected(b)}
                            data-testid={`btn-view-${b.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 py-1.5"
                          >
                            <FileText size={12} /> View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
          <span>Showing {filtered.length} of {bookings.length} bookings</span>
          {lastUpdated && (
            <>
              <span className="text-slate-300">·</span>
              <span>
                Updated {lastUpdated.toLocaleTimeString()} · auto-refreshes every 20s
              </span>
            </>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <DetailDrawer
          booking={selected}
          onClose={() => setSelected(null)}
          onUpdate={updateStatus}
          onDelete={deleteBooking}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="bg-white p-5" data-testid={testid}>
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-slate-500">
        {label}
      </div>
      <div
        className={`font-display font-black text-3xl mt-1 ${
          accent || "text-slate-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-blue-50 text-blue-700 border-blue-200",
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    completed: "bg-slate-100 text-slate-700 border-slate-300",
    cancelled: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${
        styles[status] || styles.pending
      }`}
    >
      {status}
    </span>
  );
}

function DetailDrawer({ booking, onClose, onUpdate, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="booking-drawer">
      <div
        className="absolute inset-0 bg-slate-900/50"
        onClick={onClose}
      />
      <div className="relative ml-auto h-full w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="font-display font-black text-lg tracking-tight">
            Booking details
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="p-2 hover:bg-slate-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <StatusBadge status={booking.status} />
            <div className="font-display font-black text-3xl tracking-tight mt-3">
              {booking.name}
            </div>
            <a
              href={`tel:${booking.phone}`}
              className="text-blue-600 hover:underline text-sm flex items-center gap-1 mt-1"
            >
              <Phone size={13} /> {booking.phone}
            </a>
            {booking.email && (
              <div className="text-sm text-slate-600 mt-1">{booking.email}</div>
            )}
          </div>

          <Row icon={Car} label="Service" value={booking.service} />
          <Row icon={Car} label="Vehicle" value={booking.vehicle} />
          <Row
            icon={CalIcon}
            label="When"
            value={`${booking.date} · ${booking.time}`}
          />
          <Row icon={MapPin} label="Location" value={booking.location || "-"} />
          {booking.notes && (
            <Row icon={FileText} label="Notes" value={booking.notes} />
          )}
          <div className="text-xs text-slate-400">
            Booked: {new Date(booking.created_at).toLocaleString()}
          </div>

          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-slate-500 mb-3">
              Update status
            </div>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => onUpdate(booking.id, s)}
                  disabled={booking.status === s}
                  data-testid={`set-status-${s}`}
                  className={`px-4 py-3 text-xs font-bold uppercase tracking-wider border transition-colors ${
                    booking.status === s
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white border-slate-300 hover:border-slate-900"
                  }`}
                >
                  {booking.status === s && (
                    <Check size={12} className="inline mr-1" />
                  )}
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => onDelete(booking.id)}
            data-testid="btn-delete-booking"
            className="w-full inline-flex items-center justify-center gap-2 border border-red-200 text-red-700 hover:bg-red-50 px-4 py-3 text-xs font-bold uppercase tracking-wider"
          >
            <Trash2 size={13} /> Delete booking
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-blue-600 mt-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">
          {label}
        </div>
        <div className="text-slate-900 mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}
